import {
  ArrowUpRight,
  CreditCard,
  Facebook,
  FileText,
  Globe,
  Headphones,
  Instagram,
  Mail,
  MapPin,
  MessagesSquare,
  Phone,
  Twitter as XIcon,
  Youtube,
} from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { web } from "@/config/System";

// Komponen Sosial Media Kecil (Reusable)
const SocialLink = ({ href, icon: Icon }: { href: string; icon: any }) => (
  <Link
    to={href}
    target="_blank"
    className="bg-slate-800 hover:bg-blue-600 text-slate-300 hover:text-white p-2.5 rounded-full transition-all duration-300"
  >
    <Icon className="w-4 h-4" />
  </Link>
);

// Komponen List Link Footer (Reusable)
const FooterLink = ({
  href,
  children,
  target = "_blank",
}: {
  href: string;
  children: React.ReactNode;
  target?: string;
}) => {
  const isHash = href.startsWith("#");

  if (isHash && target !== "_blank") {
    return (
      <li>
        <a
          href={href.startsWith("/#") ? href : `/${href}`}
          className="group flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm py-1.5"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-slate-600 group-hover:bg-blue-500 transition-colors" />
          {children}
        </a>
      </li>
    );
  }

  return (
    <li>
      <Link
        to={href}
        target={target}
        className="group flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm py-1.5"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-slate-600 group-hover:bg-blue-500 transition-colors" />
        {children}
      </Link>
    </li>
  );
};

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      id="kontak"
      className="w-full relative pt-24 bg-slate-50 dark:bg-slate-950 transition-colors duration-300"
    >
      {/* 1. FLOATING CONTACT CARDS SECTION (CTA) */}
      <div className="container mx-auto px-4 relative z-10 -mb-20">
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-100 dark:border-slate-800 p-8 md:p-12 transition-colors duration-300">
          <div className="flex flex-col md:flex-row gap-10 lg:gap-16">
            {/* Left: Headline & Description */}
            <div className="flex-1 space-y-6">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                Butuh Bantuan?
                <br />
                <span className="text-blue-600 dark:text-blue-500">
                  Kami siap melayani.
                </span>
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed max-w-md">
                Jangan ragu untuk menghubungi tim support kami jika Anda
                memiliki pertanyaan seputar pendaftaran atau kendala teknis.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <Button className="rounded-full px-6 h-12 text-base">
                  <Link
                    to={web.linkPermohonanInformasi}
                    target="_blank"
                    className="flex items-center gap-2"
                  >
                    <MessagesSquare className="w-4 h-4" />
                    Ajukan Pertanyaan
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full px-6 h-12 text-base border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <Link
                    to={web.linkAduan}
                    target="_blank"
                    className="flex items-center gap-2"
                  >
                    <Headphones className="w-4 h-4" />
                    Layanan Pengaduan
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right: Contact Details Grid */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Card 1: Akademik */}
              <div className="group bg-slate-50 dark:bg-slate-800/40 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-300">
                <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 flex items-center justify-center mb-5 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform duration-300">
                  <Globe className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-1">
                  Pusat Informasi
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                  Layanan info akademik & PMB
                </p>

                <div className="space-y-3 text-sm">
                  {/* Tombol Telepon */}
                  <a
                    href={`tel:${web.contactPhoneUniv.replace(/\D/g, "")}`} // Menghilangkan spasi/karakter non-angka untuk link tel:
                    className="flex items-center gap-3 p-2.5 -mx-2.5 rounded-xl hover:bg-white dark:hover:bg-slate-800/80 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:shadow-sm"
                  >
                    <div className="p-2 bg-slate-200/50 dark:bg-slate-700/50 rounded-lg text-slate-500 dark:text-slate-400">
                      <Phone className="w-4 h-4" />
                    </div>
                    <span className="flex-1">{web.contactPhoneUniv}</span>
                  </a>

                  {/* Tombol Email */}
                  <a
                    href={`mailto:${web.contactEmailUniv}`}
                    className="flex items-center gap-3 p-2.5 -mx-2.5 rounded-xl hover:bg-white dark:hover:bg-slate-800/80 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:shadow-sm break-all"
                  >
                    <div className="p-2 bg-slate-200/50 dark:bg-slate-700/50 rounded-lg text-slate-500 dark:text-slate-400">
                      <Mail className="w-4 h-4" />
                    </div>
                    <span className="flex-1">{web.contactEmailUniv}</span>
                  </a>
                </div>
              </div>

              {/* Card 2: Teknis */}
              <div className="group bg-slate-50 dark:bg-slate-800/40 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 hover:border-indigo-200 dark:hover:border-indigo-800 hover:shadow-lg hover:shadow-indigo-500/5 transition-all duration-300">
                <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 flex items-center justify-center mb-5 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform duration-300">
                  <Headphones className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-1">
                  Bantuan Teknis
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                  Layanan kendala sistem/akun
                </p>

                <div className="space-y-3 text-sm">
                  {/* Tombol Telepon / WhatsApp */}
                  <a
                    href={`https://wa.me/${web.contactPhoneTik.replace(/\D/g, "").replace(/^0/, "62")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2.5 -mx-2.5 rounded-xl hover:bg-white dark:hover:bg-slate-800/80 text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:shadow-sm"
                  >
                    <div className="p-2 bg-slate-200/50 dark:bg-slate-700/50 rounded-lg text-slate-500 dark:text-slate-400">
                      <Phone className="w-4 h-4" />
                      {/* Opsional: Anda juga bisa mengganti <Phone /> menjadi <MessageCircle /> atau icon WhatsApp dari react-icons agar lebih relevan */}
                    </div>
                    <span className="flex-1">{web.contactPhoneTik}</span>
                  </a>

                  {/* Tombol Email */}
                  <a
                    href={`mailto:${web.contactEmailTik}`}
                    className="flex items-center gap-3 p-2.5 -mx-2.5 rounded-xl hover:bg-white dark:hover:bg-slate-800/80 text-slate-700 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:shadow-sm break-all"
                  >
                    <div className="p-2 bg-slate-200/50 dark:bg-slate-700/50 rounded-lg text-slate-500 dark:text-slate-400">
                      <Mail className="w-4 h-4" />
                    </div>
                    <span className="flex-1">{web.contactEmailTik}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. MAIN FOOTER (Dark Section) */}
      <div className="bg-slate-900 pt-32 pb-12 text-slate-200 mt-0">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-16">
            {/* Brand & Address */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <img
                  src={web.logo || "/placeholder.svg"}
                  alt="Logo"
                  className="w-12 h-12 object-contain"
                />
                <div>
                  <h3 className="font-bold text-lg leading-tight text-white uppercase tracking-wider">
                    {web.univAcronym}
                  </h3>
                  <p className="text-xs text-slate-400 uppercase tracking-widest">
                    Official Campus
                  </p>
                </div>
              </div>

              <div className="space-y-3 text-sm text-slate-400 leading-relaxed">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-white block mb-1">
                      Kampus Pusat
                    </strong>
                    {web.addressStreet}, {web.address}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <SocialLink href={web.linkInstagram} icon={Instagram} />
                <SocialLink href={web.linkX} icon={XIcon} />
                <SocialLink href={web.linkFacebook} icon={Facebook} />
                <SocialLink href={web.linkYoutube} icon={Youtube} />
              </div>
            </div>

            {/* Link Group 1: Jalur Masuk */}
            <div>
              <h4 className="font-bold text-white mb-6 flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4 text-blue-500" />
                Jalur Seleksi
              </h4>
              <ul className="space-y-1">
                <FooterLink href={web.linkSNBP}>SNBP (Prestasi)</FooterLink>
                <FooterLink href={web.linkSNBT}>SNBT (Tes Tulis)</FooterLink>
                <FooterLink href={web.linkMandiri}>Seleksi Mandiri</FooterLink>
                <FooterLink href={web.linkPascasarjana}>
                  Pascasarjana
                </FooterLink>
                <FooterLink href={web.linkInformasiBeasiswa}>
                  Program Beasiswa
                </FooterLink>
              </ul>
            </div>

            {/* Link Group 2: Panduan & Info */}
            <div>
              <h4 className="font-bold text-white mb-6 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-500" />
                Panduan & Informasi
              </h4>
              <ul className="space-y-1">
                <FooterLink href="#daftar-unduhan" target="_self">
                  Panduan Pendaftaran
                </FooterLink>
                <FooterLink href={web.linkKalenderAkademik}>
                  Kalender Akademik
                </FooterLink>
                <FooterLink href={web.linkAkreditasiProgramStudi}>
                  Akreditasi Program Studi
                </FooterLink>
                <FooterLink href={web.linkFasilitasKampus}>
                  Fasilitas Kampus
                </FooterLink>
              </ul>
            </div>

            {/* Link Group 3: Pembayaran & Lainnya */}
            <div>
              <h4 className="font-bold text-white mb-6 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-500" />
                Layanan Keuangan
              </h4>
              <ul className="space-y-1">
                <FooterLink href={web.linkBiayaPendidikan}>
                  Biaya Pendidikan (UKT)
                </FooterLink>
                <FooterLink href={web.linkProsedurPembayaranBRI}>
                  Panduan Pembayaran Tagihan
                </FooterLink>

                <li className="pt-4">
                  <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <p className="text-xs text-slate-400 mb-2 font-semibold uppercase tracking-wider">
                      Hashtag Resmi
                    </p>
                    <span className="text-blue-400 font-bold">
                      #{web.tagline}
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <Separator className="bg-slate-800" />

          <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
            <p>
              © {currentYear} {web.univ}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="#" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="#" className="hover:text-white transition-colors">
                Terms of Service
              </Link>
              <Link to="#" className="hover:text-white transition-colors">
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
